import { useState } from "react";
import { motion } from "framer-motion";
import { ConnectButton } from "@rainbow-me/rainbowkit";
import { useAccount } from "wagmi";
import { formatEther, parseEther } from "viem";
import {
    Layers, Loader2, Check, AlertTriangle, ArrowUpRight, Lock, Unlock,
} from "lucide-react";
import {
    useLpStakeData, useApproveLp, useStakeLp, useUnstakeLp, useClaimLp,
} from "@/hooks/useLpStaking";
import { ADDRESSES, CHAIN_ID, EXPLORER_TX } from "@/lib/config";
import { useFireOnConfirm } from "@/hooks/useFireOnConfirm";
import { LOCK_TIER_LABELS, LOCK_TIER_MULTIPLIERS } from "@/lib/abis";
import { fmtCompact } from "@/lib/format";
import { CountUp } from "@/components/CountUp";
import { PiggyMark, EthMark } from "@/components/Logo";

export default function LP() {
    const { isConnected, chainId } = useAccount();
    const wrongChain = isConnected && chainId !== CHAIN_ID;
    const noContract = !ADDRESSES.LOCKED_LP_STAKING;
    const { data, refetch } = useLpStakeData();

    const [stakeInput, setStakeInput] = useState("");
    const [unstakeInput, setUnstakeInput] = useState("");
    const [tier, setTier] = useState(0);

    const stakeAmount = safeParse(stakeInput);
    const unstakeAmount = safeParse(unstakeInput);
    const needsApproval = stakeAmount > 0n && data.allowance < stakeAmount;

    const lockExpired = data.lockEnd === 0n || BigInt(Math.floor(Date.now() / 1000)) >= data.lockEnd;
    const lockRemainingSec = lockExpired ? 0 : Number(data.lockEnd) - Math.floor(Date.now() / 1000);

    return (
        <section className="max-w-6xl mx-auto px-6 py-16">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="text-center mb-12"
            >
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass-pink mb-4 text-xs font-semibold text-piggy-pink-200">
                    <Layers className="w-3 h-3" />
                    30% of every taxed swap → LP stakers
                </div>
                <h1 className="display text-5xl md:text-6xl font-black tracking-tighter mb-4">
                    Lock LP. <span className="gradient-text-shimmer">Multiply ETH.</span>
                </h1>
                <p className="text-piggy-cream/60 text-lg max-w-2xl mx-auto">
                    Camp out in Sherwood: stake PIGGY/WETH LP tokens, optionally
                    lock them for higher multipliers. NFT achievements stack on
                    top — Cosmic Piggy + 180-day lock = 2.5× share of the pool.
                    The longer you hold the forest, the bigger your cut.
                </p>
            </motion.div>

            {noContract ? (
                <NoticeBox>
                    LP staking contract not yet deployed. Set{" "}
                    <code className="text-piggy-pink-300">VITE_LOCKED_LP_STAKING_ADDRESS</code>{" "}
                    in your .env after testnet deploy.
                </NoticeBox>
            ) : (
                <>
                    <TierPicker selected={tier} onSelect={setTier} />

                    <div className="grid lg:grid-cols-[1fr_1fr] gap-6 mt-6">
                        <PositionCard data={data}
                                      connected={isConnected}
                                      lockExpired={lockExpired}
                                      lockRemainingSec={lockRemainingSec} />

                        <div className="space-y-6">
                            <StakeForm
                                input={stakeInput} setInput={setStakeInput}
                                amount={stakeAmount}
                                needsApproval={needsApproval}
                                balance={data.walletBalance}
                                lpToken={data.lpToken}
                                tier={tier}
                                connected={isConnected}
                                wrongChain={wrongChain}
                                onSuccess={refetch}
                            />
                            <UnstakeForm
                                input={unstakeInput} setInput={setUnstakeInput}
                                amount={unstakeAmount}
                                staked={data.stakedAmount}
                                lockExpired={lockExpired}
                                connected={isConnected}
                                wrongChain={wrongChain}
                                onSuccess={refetch}
                            />
                            <RewardsCard earned={data.earned}
                                         multiplierBps={data.effectiveMultiplierBps}
                                         connected={isConnected}
                                         wrongChain={wrongChain}
                                         onSuccess={refetch} />
                        </div>
                    </div>

                    <PoolStats data={data} />
                </>
            )}
        </section>
    );
}

function safeParse(input: string): bigint {
    try {
        if (!input || input === ".") return 0n;
        return parseEther(input);
    } catch {
        return 0n;
    }
}

// Folklore flavor names for the lock tiers — the real duration + multiplier
// stay on the card, so these are pure garnish.
const TIER_FLAVOR = ["Flex", "Scout", "Archer", "Sherwood"] as const;

function TierPicker({
    selected, onSelect,
}: { selected: number; onSelect: (t: number) => void }) {
    return (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {LOCK_TIER_LABELS.map((label, i) => {
                const active = i === selected;
                return (
                    <button
                        key={i}
                        onClick={() => onSelect(i)}
                        className={`relative rounded-3xl p-5 text-left transition-all ${
                            active
                                ? "bg-gradient-to-br from-piggy-pink-500/20 to-piggy-pink-700/20 ring-1 ring-piggy-pink-500/50 shadow-glow"
                                : "glass hover:bg-white/[0.06]"
                        }`}
                    >
                        <div className="text-xs text-piggy-cream/50 uppercase tracking-wider mb-1">
                            {TIER_FLAVOR[i] ?? `Lock ${i}`}
                        </div>
                        <div className="display text-lg font-bold text-piggy-cream mb-2">{label}</div>
                        <div className={`text-2xl font-black ${active ? "gradient-text" : "text-piggy-cream/70"}`}>
                            {LOCK_TIER_MULTIPLIERS[i]}
                        </div>
                    </button>
                );
            })}
        </div>
    );
}

function PositionCard({
    data, connected, lockExpired, lockRemainingSec,
}: {
    data: ReturnType<typeof useLpStakeData>["data"];
    connected: boolean;
    lockExpired: boolean;
    lockRemainingSec: number;
}) {
    const days   = Math.floor(lockRemainingSec / 86400);
    const hours  = Math.floor((lockRemainingSec % 86400) / 3600);

    return (
        <div className="glass rounded-3xl p-8">
            <div className="text-xs font-semibold uppercase tracking-wider text-piggy-cream/50 mb-4">
                Your LP position
            </div>

            {!connected && (
                <div className="text-xs text-piggy-cream/40 mb-4">
                    Connect your wallet to see your position.
                </div>
            )}

            <div className="space-y-4">
                <BigStat label="LP staked"
                         value={<CountUp value={Number(data.stakedAmount) / 1e18} format={(n) => fmtCompact(n, 4)} />}
                         unit="LP" />
                <BigStat label="Effective multiplier"
                         value={`${(data.effectiveMultiplierBps / 10_000).toFixed(2)}×`}
                         accent />
                <BigStat label="Unclaimed ETH"
                         value={<CountUp value={Number(data.earned) / 1e18} format={(n) => n.toFixed(5)} />}
                         unit="ETH" accent />

                <div className="rounded-2xl p-4 bg-white/[0.03] flex items-center gap-3">
                    {lockExpired ? <Unlock className="w-4 h-4 text-green-400" /> : <Lock className="w-4 h-4 text-piggy-pink-300" />}
                    <div className="flex-1">
                        <div className="text-xs text-piggy-cream/40 uppercase tracking-wider">Lock status</div>
                        <div className="display text-base font-bold text-piggy-cream">
                            {data.stakedAmount === 0n
                                ? "No stake"
                                : lockExpired
                                ? "Unlocked — withdraw anytime"
                                : `${days}d ${hours}h remaining`}
                        </div>
                    </div>
                </div>

                <div className="rounded-2xl p-4 bg-white/[0.03]">
                    <div className="text-xs text-piggy-cream/40 uppercase tracking-wider mb-1">
                        Wallet LP balance
                    </div>
                    <div className="display text-xl font-bold text-piggy-cream">
                        <CountUp value={Number(data.walletBalance) / 1e18} format={(n) => fmtCompact(n, 4)} /> LP
                    </div>
                </div>
            </div>
        </div>
    );
}

function BigStat({
    label, value, unit, accent,
}: {
    label: string; value: React.ReactNode; unit?: string; accent?: boolean;
}) {
    return (
        <div className={`rounded-2xl p-4 ${accent ? "bg-piggy-pink-500/[0.08] ring-1 ring-piggy-pink-500/30" : "bg-white/[0.03]"}`}>
            <div className="text-xs text-piggy-cream/40 uppercase tracking-wider mb-1">{label}</div>
            <div className="flex items-baseline gap-2">
                <div className={`display text-2xl font-bold ${accent ? "gradient-text" : "text-piggy-cream"}`}>
                    {value}
                </div>
                {unit && (
                    <div className="text-xs text-piggy-cream/40 inline-flex items-center gap-1.5">
                        {unit === "PIGGY" && <PiggyMark size={16} className="rounded-full" />}
                        {unit === "ETH" && <EthMark size={16} />}
                        <span>{unit}</span>
                    </div>
                )}
            </div>
        </div>
    );
}

function StakeForm({
    input, setInput, amount, needsApproval, balance, lpToken, tier, connected, wrongChain, onSuccess,
}: {
    input: string; setInput: (s: string) => void;
    amount: bigint; needsApproval: boolean; balance: bigint;
    lpToken: `0x${string}`; tier: number;
    connected: boolean; wrongChain: boolean; onSuccess: () => void;
}) {
    const approve = useApproveLp(lpToken);
    const stake = useStakeLp(amount, tier);
    const approveBusy = approve.status === "pending" || approve.status === "confirming";
    const stakeBusy = stake.status === "pending" || stake.status === "confirming";
    useFireOnConfirm(approve.status === "confirmed" || stake.status === "confirmed", onSuccess);

    const overBalance = amount > balance;

    return (
        <div className="glass rounded-3xl p-6">
            <div className="text-xs font-semibold uppercase tracking-wider text-piggy-cream/50 mb-3">
                Stake LP — {LOCK_TIER_LABELS[tier]} ({LOCK_TIER_MULTIPLIERS[tier]})
            </div>
            <AmountInput value={input} onChange={setInput}
                         onMax={() => setInput(formatEther(balance))} placeholder="0.0" />
            <div className="text-xs text-piggy-cream/40 mt-2 mb-5 flex justify-between">
                <span>LP wallet: {fmtCompact(Number(balance) / 1e18, 4)}</span>
                {overBalance && <span className="text-red-400">Exceeds balance</span>}
            </div>
            <GateButton connected={connected} wrongChain={wrongChain} action="stake LP">
                {needsApproval ? (
                    <button onClick={approve.approve} disabled={approveBusy}
                        className="w-full btn-primary justify-center disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-y-0">
                        {approveBusy && <Loader2 className="w-4 h-4 animate-spin" />}
                        {approveBusy ? "Approving..." : "Approve LP"}
                    </button>
                ) : (
                    <button onClick={stake.stake} disabled={stakeBusy || amount === 0n || overBalance}
                        className="w-full btn-primary justify-center disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-y-0">
                        {stakeBusy && <Loader2 className="w-4 h-4 animate-spin" />}
                        {stake.status === "confirmed" && <Check className="w-4 h-4" />}
                        {stakeBusy ? "Confirming..." : stake.status === "confirmed" ? "Staked!" : `Stake at ${LOCK_TIER_MULTIPLIERS[tier]}`}
                    </button>
                )}
            </GateButton>
            <TxLink hash={approve.hash || stake.hash} />
        </div>
    );
}

function UnstakeForm({
    input, setInput, amount, staked, lockExpired, connected, wrongChain, onSuccess,
}: {
    input: string; setInput: (s: string) => void;
    amount: bigint; staked: bigint; lockExpired: boolean;
    connected: boolean; wrongChain: boolean;
    onSuccess: () => void;
}) {
    const unstake = useUnstakeLp(amount);
    const busy = unstake.status === "pending" || unstake.status === "confirming";
    useFireOnConfirm(unstake.status === "confirmed", onSuccess);
    const overStake = amount > staked;

    return (
        <div className="glass rounded-3xl p-6">
            <div className="text-xs font-semibold uppercase tracking-wider text-piggy-cream/50 mb-3">
                Unstake LP
            </div>
            <AmountInput value={input} onChange={setInput}
                         onMax={() => setInput(formatEther(staked))} placeholder="0.0" />
            <div className="text-xs text-piggy-cream/40 mt-2 mb-5 flex justify-between">
                <span>Staked: {fmtCompact(Number(staked) / 1e18, 4)} LP</span>
                {!lockExpired && staked > 0n && <span className="text-piggy-pink-300">Lock active</span>}
                {overStake && <span className="text-red-400">Exceeds stake</span>}
            </div>
            <GateButton connected={connected} wrongChain={wrongChain} action="unstake LP">
                <button onClick={unstake.unstake}
                    disabled={busy || amount === 0n || overStake || !lockExpired}
                    className="w-full btn-ghost justify-center disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-y-0">
                    {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                    {unstake.status === "confirmed" && <Check className="w-4 h-4" />}
                    {busy ? "Confirming..." : !lockExpired && staked > 0n ? "Locked" : "Unstake"}
                </button>
            </GateButton>
            <TxLink hash={unstake.hash} />
        </div>
    );
}

function RewardsCard({
    earned, multiplierBps, connected, wrongChain, onSuccess,
}: { earned: bigint; multiplierBps: number; connected: boolean; wrongChain: boolean; onSuccess: () => void; }) {
    const claim = useClaimLp();
    const busy = claim.status === "pending" || claim.status === "confirming";
    useFireOnConfirm(claim.status === "confirmed", onSuccess);

    return (
        <div className="glass rounded-3xl p-6">
            <div className="flex items-baseline justify-between mb-3">
                <div className="text-xs font-semibold uppercase tracking-wider text-piggy-cream/50">
                    Claim rewards
                </div>
                <div className="text-xs text-piggy-cream/40">
                    {(multiplierBps / 10000).toFixed(2)}× active
                </div>
            </div>
            <div className="display text-4xl font-black gradient-text mb-5 inline-flex items-center gap-2">
                {formatEther(earned).slice(0, 10)} <EthMark size={28} />
                <span>ETH</span>
            </div>
            <GateButton connected={connected} wrongChain={wrongChain} action="claim">
                <button onClick={claim.claim} disabled={busy || earned === 0n}
                    className="w-full btn-gold justify-center disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-y-0">
                    {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                    {claim.status === "confirmed" && <Check className="w-4 h-4" />}
                    {busy ? "Confirming..." : claim.status === "confirmed" ? "Claimed!" : "Claim ETH"}
                </button>
            </GateButton>
            <TxLink hash={claim.hash} />
        </div>
    );
}

function AmountInput({
    value, onChange, onMax, placeholder,
}: { value: string; onChange: (s: string) => void; onMax: () => void; placeholder: string; }) {
    return (
        <div className="relative">
            <input type="text" inputMode="decimal" value={value}
                onChange={(e) => {
                    const s = e.target.value.replace(/[^0-9.]/g, "");
                    if ((s.match(/\./g) ?? []).length > 1) return;
                    onChange(s);
                }}
                placeholder={placeholder}
                className="w-full px-4 py-4 pr-20 rounded-2xl bg-white/[0.04] border border-white/10 focus:border-piggy-pink-500/40 outline-none text-2xl font-bold display text-piggy-cream"
            />
            <button onClick={onMax}
                className="absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1.5 rounded-xl bg-piggy-pink-500/20 hover:bg-piggy-pink-500/30 text-piggy-pink-200 text-xs font-semibold">
                MAX
            </button>
        </div>
    );
}

function TxLink({ hash }: { hash?: `0x${string}` }) {
    if (!hash) return null;
    return (
        <a href={`${EXPLORER_TX}/${hash}`} target="_blank" rel="noopener noreferrer"
            className="mt-3 flex items-center justify-center gap-1 text-xs text-piggy-cream/50 hover:text-piggy-cream">
            View on Explorer <ArrowUpRight className="w-3 h-3" />
        </a>
    );
}

function PoolStats({ data }: { data: ReturnType<typeof useLpStakeData>["data"] }) {
    return (
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.6 }}
            className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="glass rounded-2xl p-6">
                <div className="text-xs font-semibold uppercase tracking-wider text-piggy-cream/50 mb-2">
                    Total LP staked
                </div>
                <div className="display text-2xl font-bold text-piggy-cream">
                    <CountUp value={Number(data.totalStaked) / 1e18} format={(n) => fmtCompact(n, 2)} /> LP
                </div>
            </div>
            <div className="glass rounded-2xl p-6">
                <div className="text-xs font-semibold uppercase tracking-wider text-piggy-cream/50 mb-2">
                    Total weighted stake
                </div>
                <div className="display text-2xl font-bold text-piggy-cream">
                    <CountUp value={Number(data.totalWeightedStake) / 1e18} format={(n) => fmtCompact(n, 2)} /> LP
                </div>
            </div>
            <div className="glass rounded-2xl p-6">
                <div className="text-xs font-semibold uppercase tracking-wider text-piggy-cream/50 mb-2">
                    Lifetime ETH distributed
                </div>
                <div className="display text-2xl font-bold gradient-text inline-flex items-center gap-2">
                    <CountUp value={Number(data.totalReceived) / 1e18} format={(n) => n.toFixed(4)} />
                    <EthMark size={20} />
                    <span>ETH</span>
                </div>
            </div>
        </motion.div>
    );
}

// Shared action-button gate: disconnected → connect; wrong chain → switch;
// otherwise the real action button(s) passed as children.
function GateButton({
    connected, wrongChain, action, children,
}: {
    connected: boolean; wrongChain: boolean; action: string; children: React.ReactNode;
}) {
    if (!connected) {
        return (
            <ConnectButton.Custom>
                {({ openConnectModal }) => (
                    <button onClick={openConnectModal} className="w-full btn-primary justify-center">
                        Connect wallet to {action}
                    </button>
                )}
            </ConnectButton.Custom>
        );
    }
    if (wrongChain) {
        return (
            <ConnectButton.Custom>
                {({ openChainModal }) => (
                    <button onClick={openChainModal} className="w-full btn-primary justify-center">
                        Switch to Robinhood Chain
                    </button>
                )}
            </ConnectButton.Custom>
        );
    }
    return <>{children}</>;
}

function NoticeBox({ children }: { children: React.ReactNode }) {
    return (
        <div className="glass-pink rounded-3xl p-6 flex gap-3 items-start max-w-2xl mx-auto">
            <AlertTriangle className="w-5 h-5 text-piggy-pink-300 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-piggy-cream/80">{children}</div>
        </div>
    );
}
